import "./App.css";

import mapboxgl from "mapbox-gl";
import mixpanel from "mixpanel-browser";
import posthog from "posthog-js";
import React, { Suspense, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Navigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import Loader from "./components/loader/loader.component";
import ToastNotification from "./components/toastNotification/ToastNOtification";
import CacheBuster from "./helper/cachebuster";
import useCognitoTokenExchange from "./hooks/useCognitoTokenExchange";
import { trackLogin as trackLoginPostHog } from "./hooks/usePostHog";
import useSilentCognitoAuth from "./hooks/useSilentCognitoAuth";
import { selectToken } from "./redux/token/token.selector";
import { setCurrentUser } from "./redux/user/user.action";
import { selectCurrentUser } from "./redux/user/user.selector";
import Router from "./route/router";
import { getUser } from "./services/authentication.service";

mixpanel.init(import.meta.env.VITE_REACT_APP_MIXPANEL_TOKEN);
posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN, {
	api_host: "https://us.i.posthog.com",
	// We fire page views manually in Router.jsx so we disable the automatic one here
	capture_pageview: false,
});

mapboxgl.accessToken = import.meta.env.VITE_REACT_APP_MAPBOX_API_KEY
	? import.meta.env.VITE_REACT_APP_MAPBOX_API_KEY
	: "";

function App(props) {
	const loading = () => <Loader />;

	const getCurrentUser = useCallback(() => props.user, [props.user]);
	const getToken = useCallback(() => props.token, [props.token]);

	return (
		<BrowserRouter>
			<Suspense fallback={loading()}>
				<Router props={{ user: getCurrentUser(), token: getToken() }} />
				<ToastNotification />
			</Suspense>
		</BrowserRouter>
	);
}

const mapStateToProps = createStructuredSelector({
	token: selectToken,
	user: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
	setcurrentuser: (user) => dispatch(setCurrentUser(user)),
});

const AppWithCacheBuster = connect(
	mapStateToProps,
	mapDispatchToProps,
)((props) => {
	const { setcurrentuser } = props;

	const { isExchanging } = useCognitoTokenExchange();

	const { isCheckingCognito } = useSilentCognitoAuth(props.token, props.user);

	useEffect(() => {
		const getData = async () => {
			if (isExchanging) {
				return;
			}

			if (!!props.token && !props.user) {
				// User has a token but no user object -- fetch them from the server
				const res = await getUser();
				if (res?.data?.response_type === "success") {
					setcurrentuser(res.data.data);
					// Session restored from server -- identify in PostHog with fetched user
					trackLoginPostHog(res.data.data, "session");
				} else {
					return <Navigate to={"/login"} replace />;
				}
			} else if (!!props.token && !!props.user) {
				// User is already fully loaded in Redux from a previous session
				// Identify them in PostHog so pageviews are not recorded as anonymous
				trackLoginPostHog(props.user, "session");
			}
		};
		getData();
	}, [props.token, props.user, setcurrentuser, isExchanging]);

	useEffect(() => {
		document.title = import.meta.env.VITE_APP_NAME;
	}, []);

	return (
		<CacheBuster>
			{({ cacheloading, isLatestVersion, refreshCacheAndReload }) => {
				useEffect(() => {
					if (!cacheloading && !isLatestVersion) {
						refreshCacheAndReload();
					}
				}, [cacheloading, isLatestVersion, refreshCacheAndReload]);

				if (cacheloading) return null;

				if (isExchanging) {
					return <Loader />;
				}

				if (isCheckingCognito) {
					return <Loader />;
				}

				return <App {...props} />;
			}}
		</CacheBuster>
	);
});

export default AppWithCacheBuster;
